import { useEffect, useRef } from 'react';
import { isAtTabEdge, tabbablesIn } from './tabbable';
import { inertOutside, isHeldInert } from './inertOutside';

/**
 * Escape / outside-pointer-down / focus-restore for every dismissable layer in
 * the design system.
 *
 * `Escape` appeared **zero** times under `src/Components/DesignSystem/` before
 * 2026-08-23: no `Dialog`, `SidePanel`, `Menu`, `Dropdown` or `DropdownButton`
 * could be dismissed from the keyboard. `Dialog` and `Menu` had no
 * outside-dismiss either, and nothing anywhere restored focus to the trigger, so
 * closing a dialog dropped the caret to `<body>` and a keyboard user had to Tab
 * from the top of the page again.
 *
 * Four decisions, each with a reason that is easy to get wrong:
 *
 * 1. **A LIFO stack, so only the topmost layer reacts to Escape.** Without it, a
 *    `Dropdown` inside a `Dialog` collapses both on one keypress. `stopPropagation`
 *    cannot substitute for this: sibling listeners on the same node still run
 *    (only `stopImmediatePropagation` stops those, and only ones registered
 *    later), and a capture-phase listener has already run by then — which is not
 *    hypothetical here, `src/hooks/useTable.ts` registers exactly that.
 *
 * 2. **`mousedown`, not `click`.** The two existing implementations
 *    (`Dropdown`, `DropdownButton`) already got this right and it is worth keeping
 *    deliberate: triggers open on `click`, so by the time state has updated and
 *    this effect attaches, the opening `mousedown` is long past. Listening for
 *    `click` instead races the very click that opened the layer.
 *
 * 3. **`isInside` is a caller-supplied predicate, not `ref.contains`.**
 *    `Dropdown` can render its listbox through a portal, where the menu is not a
 *    DOM descendant of the trigger at all; it identifies its own surface with
 *    `closest('[data-dropdown-portal]')`. A hook that assumed containment would
 *    silently close the menu on every click inside it.
 *
 * 4. **Escape is never trapped.** WCAG 2.1.2 forbids a keyboard trap, so the Tab
 *    cycle below wraps Tab and Shift+Tab only.
 *
 * 5. **Only the TOPMOST modal cycles Tab (2026-08-24).** Escape was stack-aware
 *    from day one; the Tab cycle was not. With two modal layers open — a Dialog
 *    above a SidePanel, where the Dialog portals to <body> and so is never a DOM
 *    descendant of the panel — both document-level handlers ran on every
 *    keypress: the covered panel saw focus "outside its layer" and yanked it
 *    home, then the dialog yanked it back to its OWN first control. Focus reset
 *    to the dialog's close button on every Tab and the dialog could not be
 *    walked. Modal layers keep their own stack; only the top one owns Tab.
 *
 * 6. **A modal layer locks the page scroll (2026-08-24).** Nothing did — the
 *    page scrolled freely behind an open Dialog/SidePanel. The lock is a
 *    module-level count (stacked modals must not unlock early), restores the
 *    caller's own inline styles rather than clearing them, and widens
 *    `padding-right` by the scrollbar gutter so the page does not shift
 *    sideways when the bar disappears. The gutter is `innerWidth − clientWidth`
 *    and skipped when `clientWidth` is 0: jsdom has no layout, and a 1024px
 *    "gutter" under test is not a gutter.
 *
 * 7. **The Tab cycle's edges are TAB STOPS (§ 362).** "Focusable" counted `tabindex="-1"`
 *    parts (a NumberField's steppers, a Combobox's toggle), hidden subtrees and every radio of
 *    a group, so Tab from the real last stop went unrecognised and left the dialog. The stops
 *    come from `tabbable.ts`, the one definition Popover uses too.
 *
 * 8. **The innermost thing takes Escape (§ 363).** Nothing asked whether the key was already
 *    used, so Escape on a react-select's open menu inside a Dialog closed the menu AND the
 *    dialog. A layer now lets it go when the focused control had an expanded popup at keydown
 *    (read in the capture phase, before the control can re-render closed) and something
 *    consumed the key; and it ignores Escape while an IME is composing.
 *
 * 9. **Still `mousedown`, still bubbling — deliberately (§ 363 records why).** Three
 *    Computed Substitutions menus (`CSTable`, `IterationTable`, `IterationSidebar`) stop
 *    `mousedown` propagation on their triggers so that pressing an open trigger does not
 *    dismiss-then-reopen; `pointerdown`, or a capture-phase listener, would break those toggles.
 *
 * 10. **The page behind a modal is `inert` (§ 410).** `aria-modal` and the Tab cycle stopped
 *    only Tab from inside the layer; a screen reader's own navigation, Safari's default Tab and
 *    a script still reached the page. `inertOutside.ts` has the keep-list (the layer and its
 *    backdrop, toasts, portalled popups, live regions, the legacy portal roots). It is applied
 *    AFTER focus moves in, so the trigger blurs the way it always did, and released BEFORE
 *    focus goes back, because an inert element cannot take focus.
 *
 * 11. **A focus restore a still-open modal blocks waits for it (§ 411).** Close a drawer while a
 *    dialog above it is still open (both unmount together, the drawer's cleanup first) and the
 *    drawer's trigger is still inert under the dialog: the focus call would be dropped and the
 *    caret end on `<body>`. The target waits, and the dialog's close restores it when its own
 *    target has gone with the drawer.
 *
 * Out of scope, and not something this hook can arbitrate: `useTable.ts` (capture
 * phase), `useUndoRedo.ts` (on `window`) and `RenameIterationDialog.tsx` each
 * register their own Escape/keydown handlers outside the design system. They are
 * unaffected by the stack. `RenameIterationDialog` is hand-rolled rather than
 * built on this `Dialog`, so it does not double-close today.
 */

/** Tokens for the currently-open layers, outermost first. */
const openLayers: symbol[] = [];

/**
 * The open MODAL layers only, outermost first. A separate stack from
 * `openLayers` on purpose: a non-modal Dropdown open inside a Dialog sits on
 * top of `openLayers` (it must own Escape), but it must NOT suspend the
 * dialog's Tab cycle — Tab while a listbox is open still has to stay inside
 * the dialog.
 */
const modalLayers: symbol[] = [];

// ── § 363: an Escape an inner popup already took ─────────────────────────────
/*
 * Escapes whose FOCUSED control had an expanded popup (`aria-expanded="true"`) when the key
 * went down. Read in the CAPTURE phase, before any handler runs, because by the time a layer's
 * bubble listener sees the key the control may already have re-rendered its popup closed: a
 * browser runs microtasks between listeners, and React flushes a keydown's update in one.
 * Only watched while some layer listens for Escape.
 */
const escapesFromOpenPopups = new WeakSet<Event>();
const noteEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return;
  const { target } = event;
  if (target instanceof Element && target.getAttribute('aria-expanded') === 'true') escapesFromOpenPopups.add(event);
};
let escapeWatchers = 0;
const watchEscapes = (): void => {
  if (escapeWatchers === 0) document.addEventListener('keydown', noteEscape, true);
  escapeWatchers += 1;
};
const unwatchEscapes = (): void => {
  escapeWatchers -= 1;
  if (escapeWatchers === 0) document.removeEventListener('keydown', noteEscape, true);
};

// ── the body scroll lock ─────────────────────────────────────────────────────
let scrollLocks = 0;
let bodyStyleBackup: { overflow: string; paddingRight: string } | null = null;

const lockBodyScroll = (): void => {
  if (scrollLocks === 0) {
    const { clientWidth } = document.documentElement;
    // clientWidth is 0 under jsdom (no layout); a real browser never reports 0.
    const gutter = clientWidth > 0 ? window.innerWidth - clientWidth : 0;
    bodyStyleBackup = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
    };
    document.body.style.overflow = 'hidden';
    if (gutter > 0) {
      // ADD to the body's existing padding, never replace it: a body that
      // already carries `padding-right` would otherwise jump narrower by that
      // amount — the exact layout shift this compensation exists to prevent.
      const current = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${current + gutter}px`;
    }
  }
  scrollLocks += 1;
};

const unlockBodyScroll = (): void => {
  scrollLocks -= 1;
  if (scrollLocks === 0 && bodyStyleBackup) {
    document.body.style.overflow = bodyStyleBackup.overflow;
    document.body.style.paddingRight = bodyStyleBackup.paddingRight;
    bodyStyleBackup = null;
  }
};

// ── § 411: handing focus back past a modal that is still open ───────────────
/** A restore target that a still-open modal holds inert (decision 11 above). */
let blockedRestore: HTMLElement | null = null;

const restoreFocus = (back: HTMLElement | null): void => {
  // Only an element still in the document -- the trigger may have unmounted with the layer,
  // and focusing a detached node silently drops focus to <body>, the bug this prevents.
  const attached = (el: HTMLElement | null): el is HTMLElement => !!el && document.contains(el);
  if (attached(back) && !isHeldInert(back)) {
    back.focus?.();
    blockedRestore = null;
  } else if (attached(back)) {
    blockedRestore = back;
  } else if (attached(blockedRestore) && !isHeldInert(blockedRestore)) {
    blockedRestore.focus?.();
    blockedRestore = null;
  }
  if (modalLayers.length === 0) blockedRestore = null;
};

export type DismissReason = 'escape' | 'outside';

export interface UseDismissableOptions {
  isOpen: boolean;
  onDismiss: (reason: DismissReason) => void;
  /**
   * Is this node part of the layer? Pointer-downs that satisfy this are not
   * "outside". Required when `closeOnOutsidePointerDown` is on.
   */
  isInside?: (target: Node) => boolean;
  /**
   * Modal layers (`Dialog`, `SidePanel`) pull focus in on open, cycle Tab within
   * the layer, and hand focus back on close. Non-modal popups leave focus alone.
   */
  modal?: boolean;
  /** The layer itself — the element Tab is trapped inside, and the dismiss surface. */
  surface?: React.RefObject<HTMLElement | null>;
  /**
   * § 410 — a modal layer's backdrop, when it is not inside `surface`. Part of the layer, so it
   * stays live while the page behind is inert: SidePanel's closes the drawer on a click, and an
   * inert backdrop would let that click fall through to `<body>`.
   */
  backdrop?: React.RefObject<HTMLElement | null>;
  /**
   * Where to send focus on open. Point it at a REGION (not necessarily a focusable)
   * and the first focusable inside that region wins; if the region has none, the
   * `surface` itself takes focus, which needs `tabIndex={-1}` on it.
   *
   * Without this the default is "first focusable in `surface`", and for `Dialog` that
   * is the **close button** — so a keyboard-opened dialog rendered a 2px `blue-900`
   * ring around its own dismiss control, making the loudest thing in the dialog the
   * one action the user least wants, and offering "leave" as the first stop. The
   * docblock here used to claim this option existed; it did not.
   */
  initialFocus?: React.RefObject<HTMLElement | null>;
  closeOnEscape?: boolean;
  closeOnOutsidePointerDown?: boolean;
}

/**
 * Whether an `initialFocus` REGION may take focus itself, when nothing inside it can.
 * Which elements Tab reaches (the first stop on open, the ends of the Tab cycle) is the
 * § 362 seam's call: `tabbable.ts`.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function useDismissable({
  isOpen,
  onDismiss,
  isInside,
  modal = false,
  surface,
  backdrop,
  initialFocus,
  closeOnEscape = true,
  closeOnOutsidePointerDown = true,
}: UseDismissableOptions): void {
  // Held in refs so the listeners below can stay attached across re-renders
  // without being torn down and rebuilt on every parent render.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const isInsideRef = useRef(isInside);
  isInsideRef.current = isInside;

  const restoreTo = useRef<HTMLElement | null>(null);
  /** Whether THIS hook added `tabindex` to the layer, so it removes only its own. */
  const addedTabIndex = useRef(false);

  // ── the LIFO stack ────────────────────────────────────────────────────────
  const token = useRef<symbol | null>(null);
  if (!token.current) token.current = Symbol('dismissable-layer');

  useEffect(() => {
    if (!isOpen) return;
    const id = token.current!;
    openLayers.push(id);
    return () => {
      const at = openLayers.lastIndexOf(id);
      if (at !== -1) openLayers.splice(at, 1);
    };
  }, [isOpen]);

  // ── Escape, topmost layer only ────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const id = token.current!;

    watchEscapes();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      /* § 363 — mid-composition, Escape cancels the IME's candidate text, never the layer
         (keyCode 229 is how Safari reports a key the IME consumed). */
      if (event.isComposing || event.keyCode === 229) return;
      if (openLayers[openLayers.length - 1] !== id) return;
      /* § 363 — the innermost thing takes Escape. A control inside this layer whose OWN popup
         was open, and that consumed the key (react-select closing its menu calls
         `preventDefault`), has had its Escape: closing the dialog around it too threw away
         the form. Both conditions, because react-select prevents EVERY Escape — with its
         menu closed, the next Escape still has to reach the layer. No DS component consumes
         its own Escape; each one is a layer on this stack. */
      if (event.defaultPrevented && escapesFromOpenPopups.has(event)) return;
      onDismissRef.current('escape');
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      unwatchEscapes();
    };
  }, [isOpen, closeOnEscape]);

  // ── outside pointer-down ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !closeOnOutsidePointerDown) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (isInsideRef.current?.(target)) return;
      onDismissRef.current('outside');
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen, closeOnOutsidePointerDown]);

  // ── modal: pull focus in, cycle Tab, hand it back ─────────────────────────
  useEffect(() => {
    if (!isOpen || !modal) return;

    const id = token.current!;
    modalLayers.push(id);
    lockBodyScroll();

    const previous = document.activeElement as HTMLElement | null;
    restoreTo.current = previous;
    addedTabIndex.current = false;

    const root = surface?.current ?? null;
    /** § 410 — releases the page this layer made inert (set below, once focus is inside). */
    let releaseInert: (() => void) | null = null;
    if (root) {
      /*
        Order of preference: a focusable inside the requested region, then the region
        itself if it is focusable, then the layer. Deliberately NOT "first focusable
        in the layer" as a final fallback — that is what put focus on the close
        button, and a dialog with no focusable content should land on the panel (so a
        screen reader reads its accessible name) rather than on Dismiss.
      */
      const region = initialFocus?.current ?? null;
      let target: HTMLElement | null = null;
      /* § 362 — the first TAB STOP, not the first focusable: a roving item's
         `tabindex="-1"` sibling (an unchecked segment, an inactive tab) is focusable but is
         not where Tab would have started. */
      if (region) {
        const [inRegion] = tabbablesIn(region);
        target = inRegion ?? (region.matches(FOCUSABLE) ? region : root);
      } else {
        const [first] = tabbablesIn(root);
        target = first ?? root;
      }
      /*
        The layer is made focusable ONLY when it is actually the focus target, and the
        attribute is torn down again on close.

        A static `tabIndex={-1}` looks harmless and is not: a `tabindex="-1"` element
        is focusable BY CLICK, so a click anywhere on the dialog's padding landed focus
        on the PANEL, which then painted the UA focus outline around the whole surface.
        It shipped that way for one turn (§ 32) and the ring was visible in a
        screenshot.

        (Not the same as stealing the caret — clicking non-focusable padding blurs the
        field either way; that is ordinary browser behaviour. What the tabindex added
        was a focusable target for that click, and therefore a ring.)

        Doing it here instead means a form dialog's panel never becomes focusable at
        all; and a dialog with no focusable content still gets focus, and therefore
        Escape, because `.focus()` on a plain div is a no-op.
      */
      if (target === root && !root.hasAttribute('tabindex')) {
        root.setAttribute('tabindex', '-1');
        addedTabIndex.current = true;
      }
      target.focus?.();
      /* § 410 — then the page behind goes inert. After the focus move, not before: inerting
         the focused trigger first would blur it to <body> on the way in. */
      releaseInert = inertOutside([root, backdrop?.current]);
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !root) return;
      // Same rule as Escape: with modals stacked, only the top one owns Tab.
      if (modalLayers[modalLayers.length - 1] !== id) return;
      /* § 362 — the stops Tab actually visits, for the direction it is going (a radio group
         with nothing checked is entered at its first radio going forward, its last going
         back). They used to be "focusables", which counted `tabindex="-1"` parts — a
         NumberField's steppers, a Combobox's toggle — and hidden subtrees as the edge, so
         Tab from the real last stop went unrecognised and left the dialog. */
      const direction = event.shiftKey ? 'backward' : 'forward';
      const stops = tabbablesIn(root, direction);
      if (stops.length === 0) return;
      const active = document.activeElement;

      /*
        § 304 — a PORTALLED design-system surface (a Dropdown listbox, a Combobox list,
        a Popover, a DropdownButton menu — all marked `[data-dropdown-portal]`) belongs
        to the layer that opened it even though it is not a DOM descendant of the
        panel. Focus inside one has not "escaped": the surface owns its own Tab (it
        closes and returns focus to its trigger, which IS in the panel), and yanking
        focus back to the panel's first control mid-picker was the fault this closes.
      */
      if (active instanceof Element && active.closest('[data-dropdown-portal]')) return;

      // Wrap at both ends, and pull focus back in if it has escaped the layer.
      const escaped = !(active instanceof Element) || !root.contains(active);
      if (escaped || isAtTabEdge(stops, active, direction)) {
        event.preventDefault();
        (direction === 'forward' ? stops[0] : stops[stops.length - 1]).focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const at = modalLayers.lastIndexOf(id);
      if (at !== -1) modalLayers.splice(at, 1);
      unlockBodyScroll();
      // § 410 — release the page BEFORE handing focus back: an inert element cannot take focus.
      releaseInert?.();
      const back = restoreTo.current;
      restoreTo.current = null;
      // Give back the `tabindex` we borrowed, so a reopened dialog re-decides
      // rather than inheriting a stale attribute.
      if (addedTabIndex.current) {
        root?.removeAttribute('tabindex');
        addedTabIndex.current = false;
      }
      restoreFocus(back);
    };
  }, [isOpen, modal, surface, backdrop, initialFocus]);
}
